import React, { useCallback, useEffect, useState } from "react";
import api, { fileUrl, formatTanggal, uploadFile } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { FileText, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const F_EMPTY = { siswa: "", tahun_ajaran: "", semester: "" };
const TAHUN_OPTS = ["2023/2024", "2024/2025", "2025/2026", "2026/2027"];
const SEMESTER_OPTS = [
  { v: "tengah_semester_1", label: "Tengah Semester 1" },
  { v: "akhir_semester_1", label: "Akhir Semester 1" },
  { v: "tengah_semester_2", label: "Tengah Semester 2" },
  { v: "akhir_semester_2", label: "Akhir Semester 2" },
  { v: "I", label: "Semester I (lama)" },
  { v: "II", label: "Semester II (lama)" },
];
const SEM_LABEL = Object.fromEntries(SEMESTER_OPTS.map((s) => [s.v, s.label]));

export default function RaporKepsek() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [editItem, setEditItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { try { const { data } = await api.get("/rapor"); setAll(data); } catch (e) { console.error(e); } }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((r) => {
    if (applied.siswa && !(r.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.tahun_ajaran && r.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && r.semester !== applied.semester) return false;
    return true;
  });

  const del = async (id) => {
    if (!window.confirm("Hapus rapor ini?")) return;
    try { await api.delete(`/rapor/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
  };

  const uploadNewFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await uploadFile(f); setEditItem({ ...editItem, file_id: r.id }); toast.success("File baru diunggah"); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal upload"); }
    finally { setUploading(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/rapor/${editItem.id}`, {
        siswa_id: editItem.siswa_id,
        tahun_ajaran: editItem.tahun_ajaran,
        semester: editItem.semester,
        file_id: editItem.file_id,
      });
      toast.success("Rapor diperbarui");
      setEditItem(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Rapor (Semua)" />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="rapor-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari siswa" },
          { key: "tahun_ajaran", type: "select", label: "TA", options: TAHUN_OPTS },
          { key: "semester", type: "select", label: "Sem", options: SEMESTER_OPTS },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Tanggal Upload</th>
            <th className="text-left px-5 py-3">Siswa</th>
            <th className="text-left px-5 py-3">Periode</th>
            <th className="text-left px-5 py-3">File</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">Belum ada rapor.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="pp-row">
                <td className="px-5 py-3 text-xs">{formatTanggal(r.created_at)}</td>
                <td className="px-5 py-3 font-medium">{r.nama_siswa}</td>
                <td className="px-5 py-3">{r.tahun_ajaran} · {SEM_LABEL[r.semester] || `Sem ${r.semester}`}</td>
                <td className="px-5 py-3"><a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Lihat Rapor</a></td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditItem({ ...r })} className="pp-btn-ghost text-xs mr-1" data-testid={`rapor-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                  <button onClick={() => del(r.id)} className="pp-btn-danger" data-testid={`rapor-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="rapor-edit-modal">
          <div className="pp-card p-6 w-full max-w-lg animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Edit Rapor — {editItem.nama_siswa}</h3>
              <button onClick={() => setEditItem(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <label className="pp-label">Tahun Ajaran</label>
                <select className="pp-input" value={editItem.tahun_ajaran} onChange={(e) => setEditItem({ ...editItem, tahun_ajaran: e.target.value })} data-testid="rapor-edit-ta">
                  {TAHUN_OPTS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="pp-label">Semester</label>
                <select className="pp-input" value={editItem.semester} onChange={(e) => setEditItem({ ...editItem, semester: e.target.value })} data-testid="rapor-edit-sem">
                  {SEMESTER_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="pp-label">Ganti File PDF (opsional)</label>
                <label className="pp-btn-secondary cursor-pointer">
                  <Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : "Pilih PDF baru"}
                  <input type="file" accept=".pdf" onChange={uploadNewFile} className="hidden" data-testid="rapor-edit-file" />
                </label>
                <span className="ml-3 text-xs text-slate-500">File saat ini tetap dipakai jika tidak diganti</span>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="pp-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="rapor-edit-save">{saving ? "Menyimpan…" : "Simpan"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
